import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Button, Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { EPISODIC_WINDOW_DAYS } from "encamp-shared/src/constants/wasteAccumulation";
import { WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../../Containers/containerDisplay";
import { FACILITY_EPISODIC_EVENTS_QUERY } from "../../EpisodicEvents/api";
import {
  episodicReliefAdvisory,
  EVENT_TYPE_LABELS,
  formatPounds,
  isEventWindowLapsed,
} from "../../EpisodicEvents/episodicDisplay";
import { useWasteFacilityState } from "./WasteFacilityContext";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Start-date bounds for the events whose 60-day window touches the reader's
 * calendar year: one that began late last year and runs into January counts,
 * one that closed before January 1 and one dated next year do not. The floor
 * is the attach window, not a display choice: nothing shipped this year can
 * join an event that started earlier than it.
 */
export function eventWindowsTouchingYear(today = new Date()): {
  startedOnOrAfter: string;
  startedBefore: string;
} {
  const year = today.getFullYear();
  const floor = new Date(Date.UTC(year, 0, 1) - EPISODIC_WINDOW_DAYS * DAY_MS);
  return { startedOnOrAfter: floor.toISOString().slice(0, 10), startedBefore: `${year + 1}-01-01` };
}

/**
 * Dashboard summary of the facility's episodic events. Plural on purpose: a
 * PLANNED and an UNPLANNED event can overlap.
 *
 * With container tracking the events are the open ones: status is real there,
 * the nightly sweep finalizes an event once its waste has shipped, and an open
 * event past its deadline reads Expired. Without it status never moves and an
 * event is a ledger entry manifests are linked against, so the tile shows the
 * events whose window touches this year and leaves the rest to the events page.
 */
export const WasteEpisodicEventsTile = ({
  containerTrackingEnabled,
}: {
  containerTrackingEnabled: boolean;
}) => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_EPISODIC_EVENTS_QUERY, {
    variables: {
      facilityId: effectiveWasteFacilityId,
      ...(containerTrackingEnabled
        ? { status: WasteEpisodicEventStatus.Active }
        : eventWindowsTouchingYear()),
      // One planned + one unplanned per year; anything past 2 would be a data
      // bug, but the list renders whatever comes back.
      pageSize: 5,
    },
    skip: !effectiveWasteFacilityId,
  });
  const events = data?.facility?.episodicEvents?.items ?? [];

  return (
    <DashboardTile size={containerTrackingEnabled ? { sm: 12, md: 6 } : { xs: 12 }}>
      <DashboardTile.Header
        title="Episodic events"
        infoTooltip="Episodic events let a facility keep the waste from a planned or unplanned event out of its generator status. Declare the event, then add the manifests that carried its waste."
      />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="flex-start">
          {loading && !data ? (
            <Skeleton variant="text" width={220} height={48} />
          ) : events.length > 0 ? (
            <Stack spacing={1.5}>
              {events.map((event) => {
                const advisory = episodicReliefAdvisory(event.relief);
                return (
                  <Stack key={event.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {event.reason}
                      </Typography>
                      <Chip size="small" label={EVENT_TYPE_LABELS[event.type]} />
                      {containerTrackingEnabled &&
                        isEventWindowLapsed(event.accumulationDeadline) && (
                          <Chip size="small" color="error" label="Expired" />
                        )}
                      {advisory && (
                        <Tooltip title={advisory.body}>
                          <Chip size="small" variant="outlined" label={advisory.chipLabel} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {containerTrackingEnabled
                        ? `Removal deadline ${formatContainerDate(event.accumulationDeadline)}`
                        : `Manifests shipped on or after ${formatContainerDate(event.startDate)} and through ${formatContainerDate(event.accumulationDeadline)} qualify`}
                    </Typography>
                    {!containerTrackingEnabled &&
                      event.manifestExemptions.totalExemptPounds > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {formatPounds(event.manifestExemptions.totalExemptPounds)} declared
                        </Typography>
                      )}
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" color="text.secondary">
              {containerTrackingEnabled
                ? "No active episodic event"
                : "No episodic events this year"}
            </Typography>
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/episodic-events?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
          >
            View episodic events
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};
